import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { knowledgebaseService } from '../services/knowledgebaseService';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { Separator } from '../components/ui/separator';
import { 
  ArrowLeft, 
  BookOpen, 
  Calendar,
  Clock,
  Users
} from 'lucide-react';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '../components/ui/breadcrumb';
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from '../components/ui/hover-card';

const KnowledgeBaseDetails = () => {
  const { documentId } = useParams();
  const navigate = useNavigate();
  const [document, setDocument] = useState(null);
  const [dependentAgents, setDependentAgents] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch document details
  const fetchDocumentDetails = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await knowledgebaseService.getDocumentById(documentId);
      setDocument(response);
      
      // Fetch dependent agents
      if (response?.id) {
        try {
          const agentsResponse = await knowledgebaseService.getDependentAgents(response.id);
          setDependentAgents(agentsResponse?.agents || []);
        } catch (err) {
          console.error('Failed to fetch dependent agents:', err);
          setDependentAgents([]);
        }
      }
    } catch (err) {
      setError(`Failed to load document: ${err.message}`);
    } finally {
      setIsLoading(false);
    }
  }, [documentId]);

  useEffect(() => {
    if (documentId) {
      fetchDocumentDetails();
    }
  }, [documentId, fetchDocumentDetails]);


  // Format date
  const formatDate = (timestamp) => {
    if (!timestamp) return 'Unknown';
    return new Date(timestamp * 1000).toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };


  if (error) {
    return (
      <div className="min-h-screen bg-background p-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center gap-4 mb-6">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/knowledge-base')}
              className="flex items-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Knowledge Base
            </Button>
          </div>
          <div className="bg-red-50 dark:bg-red-950 text-red-700 dark:text-red-300 p-6 rounded-lg border border-red-200 dark:border-red-800">
            {error}
          </div>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex flex-col">
        {/* Header Skeleton */}
        <div className="bg-sidebar sticky top-0 z-10">
          <div className="flex h-[73px] items-center justify-between px-8">
            <div className="flex items-center gap-4">
              <Skeleton className="h-8 w-32" />
              <Skeleton className="h-4 w-48" />
            </div>
          </div>
        </div>
        <Separator className="w-full" />

        <div className="flex-1 px-8 py-8 space-y-6">
          <div className="max-w-4xl mx-auto space-y-6">
            <Skeleton className="h-8 w-64" />
            <Skeleton className="h-32 w-full" />
            <Skeleton className="h-32 w-full" />
          </div>
        </div>
      </div>
    );
  }

  if (!document) {
    return (
      <div className="min-h-screen bg-background p-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center gap-4 mb-6">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/knowledge-base')}
              className="flex items-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Knowledge Base
            </Button>
          </div>
          <div className="text-center py-12">
            <h1 className="text-2xl font-bold text-card-foreground mb-2">Document Not Found</h1>
            <p className="text-muted-foreground">The requested document could not be found.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header with breadcrumb */}
      <div className="bg-sidebar border-b border-border/60 sticky top-0 z-10">
        <div className="flex h-16 items-center justify-between px-8">
          {/* Left side - Breadcrumb aligned to left edge */}
          <div className="flex items-center">
            <Breadcrumb>
              <BreadcrumbList>
                <BreadcrumbItem>
                  <BreadcrumbLink
                    asChild
                    className="text-sm font-medium hover:text-primary cursor-pointer text-muted-foreground"
                  >
                    <button onClick={() => navigate('/knowledge-base')}>
                      Knowledge Base
                    </button>
                  </BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator className="text-muted-foreground" />
                <BreadcrumbItem>
                  <BreadcrumbPage className="text-sm font-medium text-card-foreground">
                    {document.name || 'Untitled Document'}
                  </BreadcrumbPage>
                </BreadcrumbItem>
              </BreadcrumbList>
            </Breadcrumb>
          </div>
        </div>
      </div>
      <Separator className="w-full" />

      <div className="flex-1 px-8 py-8">
        <div className="max-w-4xl mx-auto space-y-6">
          {/* Document Header Card */}
          <Card className="border-border bg-card shadow-none">
            <CardContent className="p-6">
              <div className="space-y-2">
                <h1 className="text-2xl font-bold text-card-foreground tracking-tight">
                  {document.name || 'Untitled Document'}
                </h1>
                <p className="text-sm text-muted-foreground font-mono">
                  ID: {document.id}
                </p>
              </div>
              
              <div className="space-y-4 mt-6">
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Calendar className="w-4 h-4" />
                    <span>Created</span>
                  </div>
                  <p className="text-sm font-medium">
                    {formatDate(document.metadata?.created_at_unix_secs)}
                  </p>
                </div>
                
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Clock className="w-4 h-4" />
                    <span>Last Updated</span>
                  </div>
                  <p className="text-sm font-medium">
                    {formatDate(document.metadata?.last_updated_at_unix_secs)}
                  </p>
                </div>
                
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Users className="w-4 h-4" />
                    <span>Dependent Agents</span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {dependentAgents.length > 0 ? (
                      dependentAgents.map((agent, index) => (
                        <HoverCard key={index}>
                          <HoverCardTrigger asChild>
                            <button
                              onClick={() => navigate(`/agents/${agent.id}`)}
                              className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 hover:underline cursor-pointer"
                            >
                              {agent.name}
                            </button>
                          </HoverCardTrigger>
                          <HoverCardContent className="w-80">
                            <div className="space-y-2">
                              <h4 className="text-sm font-semibold">{agent.name}</h4>
                              <div className="space-y-1 text-xs text-muted-foreground">
                                <p><span className="font-medium">ID:</span> {agent.id}</p>
                                <p><span className="font-medium">Type:</span> {agent.type || 'Unknown'}</p>
                              </div>
                            </div>
                          </HoverCardContent>
                        </HoverCard>
                      ))
                    ) : (
                      <p className="text-sm text-muted-foreground">No dependent agents</p>
                    )}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Content Card */}
          <Card className="border-border bg-card shadow-none">
            <CardHeader>
              <CardTitle className="text-lg font-semibold">
                {document.type === 'url' ? 'URL Content' : 
                 document.type === 'text' ? 'Text Content' : 
                 document.type === 'file' ? 'File Content' : 'Document Content'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {document.extracted_inner_html ? (
                <div 
                  className="prose prose-sm max-w-none prose-headings:text-card-foreground prose-p:text-card-foreground prose-a:text-blue-600 dark:prose-a:text-blue-400 prose-strong:text-card-foreground prose-code:text-card-foreground prose-pre:bg-muted prose-blockquote:text-muted-foreground"
                  dangerouslySetInnerHTML={{ __html: document.extracted_inner_html }}
                />
              ) : (
                <div className="text-center py-8">
                  <div className="w-16 h-16 bg-muted rounded-lg flex items-center justify-center mx-auto mb-4">
                    <BookOpen className="w-8 h-8 text-muted-foreground" />
                  </div>
                  <h3 className="text-lg font-semibold text-card-foreground mb-2">No Content Available</h3>
                  <p className="text-muted-foreground">
                    The document content has not been extracted yet or is not available.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default KnowledgeBaseDetails;
